import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function getPills(brand, rec) {
  const d = rec.deliverables||{};
  const posts = (d.w1Posts||0)+(d.w2Posts||0)+(d.w3Posts||0)+(d.w4Posts||0);
  const wu = rec.weeklyUpdates||{};
  const wDone = Object.values(wu).filter(Boolean).length;
  const pr = brand.targetPosts > 0 ? Math.round((posts/brand.targetPosts)*100) : null;
  const pending = (rec.tasks||[]).filter(t=>t.status!=='done'&&t.status!=='cancelled').length;
  return { cpD: rec.contentPlanToDesigner, cpC: rec.contentPlanToClient, rep: rec.monthlyReportSent, wDone, pr, posts, pending };
}

const listVariants = { animate: { transition: { staggerChildren: 0.04 } } };
const rowVariants  = { initial: { opacity:0, x:-12 }, animate: { opacity:1, x:0, transition:{ duration:.25, ease:[.4,0,.2,1] } } };

export default function ManagerView({ store, managerId, navigate }) {
  const { state, getMonthRecord, addBrand } = store;
  const { managers, brands, currentMonth } = state;
  const mgr = managers.find(m => m.id === managerId);
  const mBrands = brands.filter(b => b.managerId === managerId);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [nb, setNb] = useState({ name:'', targetPosts:8, targetVideos:0 });

  if (!mgr) return null;
  const filtered = mBrands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));
  const fmtM = currentMonth ? new Date(currentMonth+'-01').toLocaleDateString('en-GB',{month:'long',year:'numeric'}) : '';

  let cpD=0, cpC=0, rep=0, pending=0;
  mBrands.forEach(b => {
    const r = getMonthRecord(b.id, currentMonth);
    if(r.contentPlanToDesigner) cpD++;
    if(r.contentPlanToClient)   cpC++;
    if(r.monthlyReportSent)     rep++;
    pending += (r.tasks||[]).filter(t=>t.status!=='done'&&t.status!=='cancelled').length;
  });

  const handleAdd = () => {
    if (!nb.name.trim()) return;
    addBrand({ name:nb.name.trim(), managerId, targetPosts:parseInt(nb.targetPosts)||0, targetVideos:parseInt(nb.targetVideos)||0 });
    setNb({ name:'', targetPosts:8, targetVideos:0 });
    setShowAdd(false);
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
          <div className="mgr-avatar" style={{ background:mgr.color, width:44, height:44, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Syne,sans-serif', fontSize:16, fontWeight:700, color:'#000', flexShrink:0 }}>
            {mgr.name.slice(0,2).toUpperCase()}
          </div>
          <div>
            <h1 className="page-title" style={{ marginBottom:0 }}>{mgr.name}</h1>
            <p style={{ fontSize:13, color:'var(--text2)' }}>{mBrands.length} brands · {fmtM}</p>
          </div>
        </div>
      </div>

      <div className="stat-strip" style={{ gridTemplateColumns:'repeat(4,1fr)' }}>
        {[
          { val:`${cpD}/${mBrands.length}`, lbl:'→ Designer', cls:cpD===mBrands.length?'ok':'warn' },
          { val:`${cpC}/${mBrands.length}`, lbl:'→ Client',   cls:cpC===mBrands.length?'ok':'warn' },
          { val:`${rep}/${mBrands.length}`, lbl:'Reports',    cls:rep===mBrands.length?'ok':'warn' },
          { val:pending, lbl:'Open tasks', cls:pending>0?'warn':'ok' },
        ].map((s,i) => (
          <div key={i} className="stat-card">
            <div className={`sc-val ${s.cls}`}>{s.val}</div>
            <div className="sc-label">{s.lbl}</div>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <input className="search-box" placeholder="Search brands..." value={search} onChange={e=>setSearch(e.target.value)} />
        <motion.button className="btn btn-accent btn-sm" whileTap={{scale:.96}} onClick={() => setShowAdd(s=>!s)}>
          + Add brand
        </motion.button>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}}
            style={{ overflow:'hidden', marginBottom:12 }}
          >
            <div className="add-bar">
              <input placeholder="Brand name" value={nb.name} onChange={e=>setNb(p=>({...p,name:e.target.value}))} style={{minWidth:160}} />
              <input type="number" placeholder="Posts/mo" value={nb.targetPosts} onChange={e=>setNb(p=>({...p,targetPosts:e.target.value}))} style={{width:100}} />
              <input type="number" placeholder="Videos/mo" value={nb.targetVideos} onChange={e=>setNb(p=>({...p,targetVideos:e.target.value}))} style={{width:100}} />
              <button className="btn btn-accent btn-sm" onClick={handleAdd}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowAdd(false)}>Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div className="brand-list" variants={listVariants} initial="initial" animate="animate">
        {filtered.length === 0 && <div className="empty-state"><p>No brands found</p></div>}
        {filtered.map(b => {
          const rec = getMonthRecord(b.id, currentMonth);
          const { cpD, cpC, rep, wDone, pr, posts, pending } = getPills(b, rec);
          return (
            <motion.div
              key={b.id} variants={rowVariants}
              className="brand-item"
              whileHover={{ x:4, borderColor: mgr.color+'50', transition:{duration:.15} }}
              whileTap={{ scale:.99 }}
              onClick={() => navigate('brand', { brand: b.id })}
            >
              <div className="bi-color-dot" style={{ background: mgr.color }} />
              <div className="bi-main">
                <div className="bi-name">{b.name}</div>
                <div className="bi-pills">
                  <span className={`kpill ${cpD?'done':'miss'}`}>{cpD?'✓':'✗'} Designer</span>
                  <span className={`kpill ${cpC?'done':'miss'}`}>{cpC?'✓':'✗'} Client</span>
                  <span className={`kpill ${rep?'done':'miss'}`}>{rep?'✓':'✗'} Report</span>
                  <span className={`kpill ${wDone>=4?'done':wDone>=2?'warn':'miss'}`}>{wDone}/4 weekly</span>
                  {b.targetPosts>0 && (
                    <span className={`kpill ${pr>=100?'done':pr>=70?'warn':'miss'}`}>
                      {posts}/{b.targetPosts} posts
                    </span>
                  )}
                  {pending > 0 && <span className="badge amber">{pending} tasks</span>}
                </div>
              </div>
              <span className="bi-arrow">›</span>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
